import React from 'react';
import './SoundProofing.css';
import QuoteForm from '../../components/QuoteForm';
import SEO from '../../components/SEO';

const SoundproofingPage = () => {

  const services = [
    {
      title: 'Wall Soundproofing Solutions',
      description: 'Using acoustic boards, double-layer drywall, and insulation to block sound transmission between rooms.',
      icon: '🧱'
    },
    {
      title: 'Ceiling Soundproofing Systems',
      description: 'Ideal for apartments, offices, and studios to reduce noise from above with acoustic panels and insulation.',
      icon: '🔇'
    },
    {
      title: 'Floor Soundproofing Solutions',
      description: 'Impact noise reduction using underlays, carpets, and floating floors for quieter living spaces.',
      icon: '🏃'
    },
    {
      title: 'Office & Commercial Soundproofing',
      description: 'Improve productivity with noise control in open offices, meeting rooms, and commercial spaces.',
      icon: '🏢'
    },
    {
      title: 'Studio & Home Theater Soundproofing',
      description: 'Professional acoustic treatment for recording studios, home theaters, and media rooms.',
      icon: '🎬'
    }
  ];

  const processSteps = [
    {
      step: '1',
      title: 'Consultation & Site Assessment',
      description: 'We evaluate your space and understand your specific noise reduction needs.'
    },
    {
      step: '2',
      title: 'Noise Analysis & Recommendations',
      description: 'Comprehensive analysis to identify noise sources and recommend optimal solutions.'
    },
    {
      step: '3',
      title: 'Custom Design Planning',
      description: 'Tailored soundproofing design based on your space, budget, and requirements.'
    },
    {
      step: '4',
      title: 'Material Selection',
      description: 'Choose from premium acoustic materials that match your aesthetic and performance needs.'
    },
    {
      step: '5',
      title: 'Professional Installation',
      description: 'Expert installation by our experienced team with minimal disruption.'
    },
    {
      step: '6',
      title: 'Final Inspection & Testing',
      description: 'Thorough testing to ensure maximum noise reduction and quality standards.'
    }
  ];

  const benefits = [
    {
      title: 'Reduced Noise Pollution',
      description: 'Block unwanted external and internal noise for a peaceful environment.',
      icon: '🔈'
    },
    {
      title: 'Enhanced Privacy',
      description: 'Keep conversations confidential and prevent sound leakage between spaces.',
      icon: '🔒'
    },
    {
      title: 'Better Sleep & Comfort',
      description: 'Create quiet bedrooms and living areas for improved rest and relaxation.',
      icon: '😴'
    },
    {
      title: 'Improved Productivity',
      description: 'Boost focus and efficiency in offices with reduced noise distractions.',
      icon: '📈'
    },
    {
      title: 'Higher Property Value',
      description: 'Increase real estate value with professional soundproofing installations.',
      icon: '💎'
    },
    {
      title: 'Improved Acoustics',
      description: 'Enhance sound quality for media rooms, studios, and entertainment spaces.',
      icon: '🎵'
    }
  ];

  const applications = [
    { title: 'Apartments & Homes', icon: '🏠' },
    { title: 'Offices & Meeting Rooms', icon: '💼' },
    { title: 'Recording Studios', icon: '🎙️' },
    { title: 'Hotels & Hospitality', icon: '🏨' },
    { title: 'Schools & Institutions', icon: '🎓' },
    { title: 'Hospitals & Healthcare', icon: '🏥' }
  ];

  const materials = [
    'Acoustic panels',
    'Mineral wool insulation',
    'Soundproof drywall',
    'Mass loaded vinyl (MLV)',
    'Acoustic foam',
    'Resilient channels'
  ];

  const faqs = [
    {
      question: 'How effective are soundproofing solutions?',
      answer: 'Professional soundproofing can reduce noise by up to 70–90% depending on materials and installation quality.'
    },
    {
      question: 'What is the difference between soundproofing and acoustic treatment?',
      answer: 'Soundproofing blocks sound transmission between spaces, while acoustic treatment improves sound quality within a room by reducing echo and reverberation.'
    },
    {
      question: 'Can existing rooms be soundproofed?',
      answer: 'Yes, we offer retrofit soundproofing solutions for existing structures without requiring major renovations.'
    },
    {
      question: 'How long does soundproofing installation take?',
      answer: 'Installation time varies by project size. A single room typically takes 1-3 days, while larger spaces may take longer.'
    },
    {
      question: 'Is soundproofing expensive?',
      answer: 'We offer solutions for every budget. While premium materials cost more, we provide affordable options that still deliver excellent noise reduction.'
    }
  ];

  const testimonials = [
    {
      name: 'Michael R.',
      location: 'Nairobi',
      text: 'The soundproofing transformed our apartment. We can finally sleep peacefully without hearing traffic noise. Excellent work!',
      rating: 5
    },
    {
      name: 'Sarah K.',
      location: 'Mombasa',
      text: 'Professional team that delivered on their promises. Our home studio now has perfect acoustics for recording.',
      rating: 5
    },
    {
      name: 'David M.',
      location: 'Kisumu',
      text: 'Our office productivity improved significantly after soundproofing the meeting rooms. Highly recommend their services!',
      rating: 5
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Soundproofing Solutions",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Techie Interiors",
      "telephone": "+254721431126",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Nairobi",
        "addressCountry": "KE"
      }
    },
    "areaServed": "Kenya",
    "description": "Professional soundproofing and acoustic treatment services in Nairobi Kenya for homes, offices, studios, and commercial spaces."
  };

  return (
    <div className="soundproofing-page">
      {/* SEO Component */}
      <SEO
        title="Professional Soundproofing & Acoustic Solutions Kenya | Techie Interiors"
        description="Expert soundproofing services in Nairobi Kenya - wall, ceiling, and floor soundproofing for homes, offices, studios, and commercial spaces. Noise reduction solutions that work."
        image="/techLogo.jpeg"
        url="https://techieinteriors.co.ke/soundproofing"
        structuredData={structuredData}
      />
      {/* 1. HERO SECTION */}
      <section className="soundproofing-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Professional Soundproofing Solutions for Homes & Commercial Spaces</h1>
          <p>Reduce noise, improve privacy, and enhance comfort with our advanced soundproofing solutions tailored for residential, office, studio, and commercial environments.</p>
          <div className="hero-buttons">
            <a href="#contact-form" className="btn btn-primary">Get a Free Consultation</a>
            <a href="#contact-form" className="btn btn-secondary">Request a Quote</a>
          </div>
        </div>
      </section>

      {/* 2. INTRODUCTION SECTION */}
      <section className="soundproofing-intro">
        <div className="container">
          <div className="intro-grid">
            <div className="intro-text">
              <h2 className="section-title-left">What Are Soundproofing Solutions?</h2>
              <p>
                <strong>Soundproofing solutions</strong> involve specialized materials and techniques designed to reduce or block unwanted noise from entering or leaving a space. At Techie Interiors, we provide high-performance <strong>soundproofing systems</strong>, including acoustic panels, insulated drywall, and noise-reducing partitions.
              </p>
              <p>
                Whether you are dealing with noisy neighbors, traffic sounds, or echo issues, our custom <strong>soundproofing solutions</strong> are engineered to deliver maximum noise reduction and comfort for your home or business.
              </p>
            </div>
            <div className="intro-image">
              <div className="image">
                <img src="/techLogo.jpeg" alt="Professional soundproofing and acoustic solutions by Techie Interiors" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. SERVICES SECTION */}
      <section className="soundproofing-services">
        <div className="container">
          <h2 className="section-title">Our Soundproofing Services</h2>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. WHY CHOOSE US SECTION */}
      <section className="soundproofing-why-choose">
        <div className="container">
          <h2 className="section-title">Why Choose Techie Interiors for Soundproofing Solutions?</h2>
          <div className="why-choose-grid">
            <div className="why-choose-item">
              <div className="check-icon">✓</div>
              <h3>High-Quality Materials</h3>
              <p>Premium acoustic and soundproofing materials for optimal performance</p>
            </div>
            <div className="why-choose-item">
              <div className="check-icon">✓</div>
              <h3>Customized Solutions</h3>
              <p>Tailored soundproofing systems designed for every unique space</p>
            </div>
            <div className="why-choose-item">
              <div className="check-icon">✓</div>
              <h3>Experienced Team</h3>
              <p>Professional installation by skilled and certified technicians</p>
            </div>
            <div className="why-choose-item">
              <div className="check-icon">✓</div>
              <h3>Affordable Pricing</h3>
              <p>Competitive rates without compromising on quality or results</p>
            </div>
            <div className="why-choose-item">
              <div className="check-icon">✓</div>
              <h3>Modern Designs</h3>
              <p>Aesthetic solutions that blend seamlessly with your interior</p>
            </div>
            <div className="why-choose-item">
              <div className="check-icon">✓</div>
              <h3>Residential & Commercial</h3>
              <p>Complete soundproofing solutions for all types of properties</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. BENEFITS SECTION */}
      <section className="soundproofing-benefits">
        <div className="container">
          <h2 className="section-title">Benefits of Professional Soundproofing Solutions</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. PROCESS SECTION */}
      <section className="soundproofing-process">
        <div className="container">
          <h2 className="section-title">Our Soundproofing Installation Process</h2>
          <div className="process-steps">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{step.step}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. APPLICATIONS SECTION */}
      <section className="soundproofing-applications">
        <div className="container">
          <h2 className="section-title">Where Our Soundproofing Solutions Work Best</h2>
          <div className="applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="application-card">
                <div className="application-icon">{app.icon}</div>
                <h3>{app.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. MATERIALS SECTION */}
      <section className="soundproofing-materials">
        <div className="container">
          <div className="materials-content">
            <h2 className="section-title-left">High-Quality Soundproofing Materials</h2>
            <p>
              We use industry-leading materials to ensure maximum noise reduction and long-lasting performance. Our premium <strong>soundproofing materials</strong> include:
            </p>
            <ul className="materials-list">
              {materials.map((material, index) => (
                <li key={index}>
                  <span className="material-check">✓</span>
                  {material}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 9. FAQs SECTION */}
      <section className="soundproofing-faqs">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions About Soundproofing Solutions</h2>
          <div className="faqs-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-card">
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11. CONTACT FORM */}
      <section className="soundproofing-form-section" id="contact-form">
        <div className="container">
          <h2 className="section-title">Request a Soundproofing Quote</h2>
          <div className="form-wrapper">
            <div className="contact-info">
              <h3>Get In Touch</h3>
              <p>Have questions about our soundproofing services? We're here to help you create a quieter, more comfortable space.</p>
              <div className="info-item">
                <strong>Phone:</strong>
                <span>+254 721 431 126</span>
              </div>
              <div className="info-item">
                <strong>Email:</strong>
                <span>info@techieinteriors.co.ke</span>
              </div>
              <div className="info-item">
                <strong>Location:</strong>
                <span>Nairobi, Kenya</span>
              </div>
              <div className="trust-line">
                <strong>We respond instantly.</strong>
              </div>
            </div>
            <QuoteForm serviceName="Soundproofing Solutions" />
          </div>
        </div>
      </section>

      {/* 12. TESTIMONIALS SECTION */}
      <section className="soundproofing-trust">
        <div className="container">
          <h2 className="section-title">Trusted Across Kenya</h2>
          <p className="trust-subtitle">We've helped homeowners, businesses, and studios achieve perfect sound control</p>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="rating">{'⭐'.repeat(testimonial.rating)}</div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.location}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 13. SEO FOOTER CONTENT */}
      <section className="soundproofing-seo-footer">
        <div className="container">
          <p>
            Techie Interiors is a trusted provider of <strong>soundproofing solutions</strong>, offering advanced acoustic systems for homes, offices, and commercial spaces. Our expertise in <strong>wall soundproofing</strong>, <strong>ceiling soundproofing</strong>, and <strong>noise reduction solutions</strong> ensures optimal performance and long-lasting results. Whether you need <strong>acoustic panels</strong> for a recording studio, <strong>office soundproofing</strong> for better productivity, or <strong>home soundproofing</strong> for peace and privacy, we deliver customized solutions that meet your specific needs and budget.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SoundproofingPage;
